import React, { createContext, useContext, useEffect, useReducer } from 'react';
import { ProductAttribute, ProductVersion, Unit } from 'models';
import { SaleItemDimension } from 'models/SaleOrder';
import { useLocation } from 'react-router-dom';
import { CartStatus } from 'types/common';

export interface CartItem {
  id: string;
  productVersion: ProductVersion;
  quantity: number;
  unitConvertValue?: number;
  unit: Unit;
  unitPrice: number;
  attribute?: ProductAttribute;
  totalPrice: number;
  saleItemDimesions?: SaleItemDimension[];
}

export interface Cart {
  id: string;
  name: string;
  status: CartStatus;
  items: CartItem[];
  totalAmount: number;
  discount?: number;
  tax?: number;
  code?: string;
  saleOrderId?: string;
  customer?: {
    id?: string;
    name?: string;
    phone?: string;
    email?: string;
    address?: string;
  };
  note?: string;
}

interface CartState {
  carts: Cart[];
  activeCartId: string | null;
}

type CartAction =
  | { type: 'ADD_CART'; payload: { name: string } }
  | { type: 'REMOVE_CART'; payload: { id: string } }
  | { type: 'SET_ACTIVE_CART'; payload: { id: string } }
  | { type: 'UPDATE_CART_STATUS'; payload: { id: string; status: CartStatus } }
  | { type: 'ADD_ITEM'; payload: { cartId: string; item: Omit<CartItem, 'id'> } }
  | { type: 'UPDATE_ITEM'; payload: { cartId: string; itemId: string; updates: Partial<CartItem> } }
  | { type: 'REMOVE_ITEM'; payload: { cartId: string; itemId: string } }
  | { type: 'UPDATE_CUSTOMER'; payload: { cartId: string; customer: Cart['customer'] } }
  | { type: 'UPDATE_CART_DISCOUNT'; payload: { cartId: string; discount: number } }
  | { type: 'CLEAR_CART'; payload: { cartId: string } }
  | { type: 'UPDATE_CART'; payload: { cartId: string; updates: Partial<Cart> } }
  | { type: 'RESET_ALL' };

interface CartSessionContextType {
  state: CartState;
  addCart: (name: string) => void;
  removeCart: (id: string) => void;
  setActiveCart: (id: string) => void;
  updateCartStatus: (id: string, status: CartStatus) => void;
  addItem: (
    productVersion: ProductVersion,
    unit: Unit,
    quantity: number,
    attribute?: ProductAttribute,
    unitPrice?: number,
  ) => void;
  updateItem: (itemId: string, updates: Partial<CartItem>) => void;
  removeItem: (itemId: string) => void;
  updateCustomer: (customer: Cart['customer']) => void;
  updateDiscount: (discount: number) => void;
  updateCart: (cartId: string, updates: Partial<Cart>) => void;
  clearCart: () => void;
  resetAll: () => void;
  getActiveCart: () => Cart | null;
}

// Create context
const CartSessionContext = createContext<CartSessionContextType | null>(null);

// Reducer function
const cartReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case 'ADD_CART': {
      if (state.carts.length >= 3) return state;
      const newCartId = Date.now().toString();
      const newCart: Cart = {
        id: newCartId,
        name: '#' + (action.payload.name || newCartId.substring(8, 13)),
        status: CartStatus.PRODUCT_SELECTION,
        items: [],
        totalAmount: 0,
      };

      return {
        ...state,
        carts: [...state.carts, newCart],
        activeCartId: newCart.id,
      };
    }

    case 'REMOVE_CART': {
      const updatedCarts = state.carts.filter((cart) => cart.id !== action.payload.id);
      const activeCartId =
        state.activeCartId === action.payload.id
          ? updatedCarts.length > 0
            ? updatedCarts[0].id
            : null
          : state.activeCartId;

      return {
        ...state,
        carts: updatedCarts,
        activeCartId,
      };
    }

    case 'SET_ACTIVE_CART': {
      return {
        ...state,
        activeCartId: action.payload.id,
      };
    }

    case 'UPDATE_CART_STATUS': {
      return {
        ...state,
        carts: state.carts.map((cart) =>
          cart.id === action.payload.id ? { ...cart, status: action.payload.status } : cart,
        ),
      };
    }

    case 'ADD_ITEM': {
      const { cartId, item } = action.payload;

      return {
        ...state,
        carts: state.carts.map((cart) => {
          if (cart.id !== cartId) return cart;

          const newItem: CartItem = {
            id: Date.now().toString(),
            ...item,
            totalPrice: item.quantity * item.unitPrice,
          };

          const newTotal = cart.items.reduce((sum, item) => sum + item.totalPrice, 0) + newItem.totalPrice;

          return {
            ...cart,
            items: [...cart.items, newItem],
            totalAmount: newTotal,
          };
        }),
      };
    }

    case 'UPDATE_ITEM': {
      const { cartId, itemId, updates } = action.payload;

      return {
        ...state,
        carts: state.carts.map((cart) => {
          if (cart.id !== cartId) return cart;

          const updatedItems = cart.items.map((item) => {
            if (item.id !== itemId) return item;

            const updatedItem = {
              ...item,
              ...updates,
              totalPrice:
                updates.quantity !== undefined || updates.unitPrice !== undefined
                  ? (updates.quantity !== undefined ? updates.quantity : item.quantity) *
                    (updates.unitPrice !== undefined ? updates.unitPrice : item.unitPrice)
                  : item.totalPrice,
            };

            return updatedItem;
          });

          const newTotal = updatedItems.reduce((sum, item) => sum + item.totalPrice, 0);

          return {
            ...cart,
            items: updatedItems,
            totalAmount: newTotal,
          };
        }),
      };
    }

    case 'REMOVE_ITEM': {
      const { cartId, itemId } = action.payload;

      return {
        ...state,
        carts: state.carts.map((cart) => {
          if (cart.id !== cartId) return cart;

          const updatedItems = cart.items.filter((item) => item.id !== itemId);

          const newTotal = updatedItems.reduce((sum, item) => sum + item.totalPrice, 0);

          return {
            ...cart,
            items: updatedItems,
            totalAmount: newTotal,
          };
        }),
      };
    }

    case 'UPDATE_CUSTOMER': {
      const { cartId, customer } = action.payload;

      return {
        ...state,
        carts: state.carts.map((cart) => (cart.id === cartId ? { ...cart, customer } : cart)),
      };
    }

    case 'UPDATE_CART_DISCOUNT': {
      const { cartId, discount } = action.payload;

      return {
        ...state,
        carts: state.carts.map((cart) => (cart.id === cartId ? { ...cart, discount } : cart)),
      };
    }

    case 'UPDATE_CART': {
      const { cartId, updates } = action.payload;

      return {
        ...state,
        carts: state.carts.map((cart) => {
          if (cart.id !== cartId) return cart;

          return {
            ...cart,
            ...updates,
            totalAmount: cart.items.reduce((sum, item) => sum + item.totalPrice, 0) - (updates.discount || 0),
          };
        }),
      };
    }

    case 'CLEAR_CART': {
      const { cartId } = action.payload;

      return {
        ...state,
        carts: state.carts.map((cart) => {
          if (cart.id === cartId) {
            return {
              id: cart.id,
              name: cart.name,
              status: CartStatus.PRODUCT_SELECTION,
              items: [],
              totalAmount: 0,
            };
          }
          return cart;
        }),
      };
    }

    case 'RESET_ALL': {
      return {
        carts: [],
        activeCartId: null,
      };
    }

    default:
      return state;
  }
};

// Provider component
export const CartSessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const initialState: CartState = {
    carts: [
      {
        id: Date.now().toString(),
        name: '#' + Date.now().toString().substring(8, 13),
        status: CartStatus.PRODUCT_SELECTION,
        items: [],
        totalAmount: 0,
      },
    ],
    activeCartId: Date.now().toString(),
  };

  const [state, dispatch] = useReducer(cartReducer, initialState);
  const location = useLocation();
  useEffect(() => {
    clearCart();
  }, [location.pathname]);
  const getActiveCart = (): Cart | null => {
    if (!state.activeCartId) return null;
    return state.carts.find((cart) => cart.id === state.activeCartId) || null;
  };

  const addCart = (name: string) => {
    dispatch({ type: 'ADD_CART', payload: { name } });
  };

  const removeCart = (id: string) => {
    dispatch({ type: 'REMOVE_CART', payload: { id } });
  };

  const setActiveCart = (id: string) => {
    dispatch({ type: 'SET_ACTIVE_CART', payload: { id } });
  };

  const updateCartStatus = (id: string, status: CartStatus) => {
    dispatch({ type: 'UPDATE_CART_STATUS', payload: { id, status } });
  };

  const addItem = (
    productVersion: ProductVersion,
    unit: Unit,
    quantity: number,
    attribute?: ProductAttribute,
    unitPrice?: number,
  ) => {
    const activeCart = getActiveCart();
    if (!activeCart) return;

    unitPrice = unitPrice ? unitPrice : productVersion.salePrice || 0;

    dispatch({
      type: 'ADD_ITEM',
      payload: {
        cartId: activeCart.id,
        item: {
          productVersion,
          unit,
          quantity,
          unitPrice,
          attribute,
          totalPrice: quantity * unitPrice,
        },
      },
    });
  };

  const updateItem = (itemId: string, updates: Partial<CartItem>) => {
    const activeCart = getActiveCart();
    if (!activeCart) return;

    dispatch({
      type: 'UPDATE_ITEM',
      payload: {
        cartId: activeCart.id,
        itemId,
        updates,
      },
    });
  };

  const removeItem = (itemId: string) => {
    const activeCart = getActiveCart();
    if (!activeCart) return;

    dispatch({
      type: 'REMOVE_ITEM',
      payload: {
        cartId: activeCart.id,
        itemId,
      },
    });
  };

  const updateCustomer = (customer: Cart['customer']) => {
    const activeCart = getActiveCart();
    if (!activeCart) return;

    dispatch({
      type: 'UPDATE_CUSTOMER',
      payload: {
        cartId: activeCart.id,
        customer,
      },
    });
  };

  const updateDiscount = (discount: number) => {
    const activeCart = getActiveCart();
    if (!activeCart) return;

    dispatch({
      type: 'UPDATE_CART_DISCOUNT',
      payload: {
        cartId: activeCart.id,
        discount,
      },
    });
  };

  const updateCart = (cartId: string, updates: Partial<Cart>) => {
    dispatch({
      type: 'UPDATE_CART',
      payload: {
        cartId: cartId,
        updates,
      },
    });
  };

  const clearCart = () => {
    const activeCart = getActiveCart();
    if (!activeCart) return;

    dispatch({
      type: 'CLEAR_CART',
      payload: {
        cartId: activeCart.id,
      },
    });
  };

  const resetAll = () => {
    dispatch({ type: 'RESET_ALL' });
  };

  const contextValue: CartSessionContextType = {
    state,
    addCart,
    removeCart,
    setActiveCart,
    updateCartStatus,
    addItem,
    updateItem,
    removeItem,
    updateCustomer,
    updateDiscount,
    updateCart,
    clearCart,
    resetAll,
    getActiveCart,
  };

  return <CartSessionContext.Provider value={contextValue}>{children}</CartSessionContext.Provider>;
};

// Custom hook for consuming the context
export const useCartSession = () => {
  const context = useContext(CartSessionContext);
  if (!context) {
    throw new Error('useCartSession must be used within a CartSessionProvider');
  }
  return context;
};
